import { useCallback, useEffect, useMemo, useState } from 'react';
import { CreateTaskInventory, Customer, Task } from '@bringg/types';
import { Button, notification, Progress } from 'antd';
import { ArgsProps } from 'antd/es/notification';

import { useAvailableDeliveries } from 'Hooks';
import { useAuth } from 'Providers';
import { createMultipleRandomTasksAndProcessThem } from 'Services';
import { CreateMultipleTasksYieldResult, CreateTaskArguments, ECommerceSettings } from 'Types';

import './styles.scss';

interface Props {
  customer: Customer;
  settings: ECommerceSettings;
  inventories: CreateTaskInventory[];
  createTask: (...args: CreateTaskArguments) => Promise<Task>;
}

export function BackgroundTasks({ settings, customer, inventories, createTask }: Props): null {
  const { dashboardSdk } = useAuth();
  const [backgroundTasks, setBackgroundTasks] = useState<AsyncGenerator<
    CreateMultipleTasksYieldResult,
    void,
    void
  > | null>(null);
  const [isCancelled, setCancelled] = useState(false);

  const [progress, setProgress] = useState({ finished: 0, total: 0 });
  const [status, setStatus] = useState<CreateMultipleTasksYieldResult>({ done: false });

  const [, deliveries] = useAvailableDeliveries();
  const notificationConfig = useMemo<Partial<ArgsProps>>(
    () => ({
      duration: null,
      placement: 'bottomRight',
      btn: <></>,
      key: String(Math.random()),
    }),
    []
  );

  const runNext = useCallback(async () => {
    if (backgroundTasks) {
      const { done } = (await backgroundTasks.next()) as CreateMultipleTasksYieldResult;

      setStatus({ done });
    }
  }, [backgroundTasks]);

  const onProgress = (percentages: Array<number>) => {
    const finished = percentages.filter((percent) => percent === 100).length;

    setProgress({ finished, total: percentages.length });
  };

  useEffect(() => {
    if (!notificationConfig || !backgroundTasks || isCancelled) {
      return;
    }

    const { finished, total } = progress;

    notification.open({
      ...notificationConfig,
      message: (
        <div>
          Background tasks created: {finished}/{total}
          <Progress showInfo={false} percent={(finished / total) * 100} />
          <Button
            type="primary"
            danger={true}
            onClick={() => {
              setCancelled(true);
              notification.close(notificationConfig.key as string);
            }}
          >
            Cancel
          </Button>
        </div>
      ),
    });
  }, [progress, notificationConfig, backgroundTasks, isCancelled]);

  useEffect(() => {
    if (backgroundTasks || !dashboardSdk || !deliveries.length || !customer) {
      return;
    }

    setBackgroundTasks(
      createMultipleRandomTasksAndProcessThem(
        settings,
        dashboardSdk,
        customer,
        inventories,
        deliveries,
        createTask,
        onProgress
      )
    );
  }, [dashboardSdk, deliveries, customer, backgroundTasks, inventories, settings, createTask]);

  useEffect(() => {
    if (!status.done && !isCancelled) {
      runNext();
    }
  }, [status, runNext, isCancelled]);

  return null;
}
